/**
 * Shared helpers for the entity-state rules. Rules consume the folded
 * current-value projection (`Record<entityId, EntityState>`) plus the
 * predicate registry — never the op log directly (the fold is the
 * rebuildable view; the op log stays authoritative).
 */
import { splitFactKey, type EntityState, type FactState, type FactValidity } from '@dl/shared';

/** One fact in the walk: its owning entity, map key, base predicate, state. */
export interface FactRef {
  entity: EntityState;
  /** The `entity.facts` map key — `predicate`, `predicate#instance`, or a belief key. */
  factKey: string;
  /** The base predicate (instance / belief suffix stripped) for registry lookup. */
  predicate: string;
  fact: FactState;
}

/**
 * Walk every live (not soft-deleted) fact across non-soft-deleted entities,
 * yielding the base predicate alongside the map key. Soft-deleted entities and
 * facts are skipped — they are not part of the current canon the auditor
 * reasons about.
 */
export function forEachFact(
  entities: Record<string, EntityState>,
  fn: (ref: FactRef) => void,
): void {
  for (const entity of Object.values(entities)) {
    if (entity.soft_deleted) continue;
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      fn({ entity, factKey, predicate: splitFactKey(factKey).predicate, fact });
    }
  }
}

/**
 * Do two validity ranges overlap? `null` is timeless (overlaps everything);
 * an absent bound is unbounded in that direction. Closed-range inclusivity
 * matches `FactValiditySchema` (`[from, to]` inclusive).
 */
export function validityOverlaps(a: FactValidity | null, b: FactValidity | null): boolean {
  if (a === null || b === null) return true;
  const aFrom = a.from ?? Number.NEGATIVE_INFINITY;
  const aTo = a.to ?? Number.POSITIVE_INFINITY;
  const bFrom = b.from ?? Number.NEGATIVE_INFINITY;
  const bTo = b.to ?? Number.POSITIVE_INFINITY;
  return aFrom <= bTo && bFrom <= aTo;
}

/**
 * Half-open `[from, to)` overlap test (ADR-0005 §4) — the convention valid-time
 * SEGMENTS are stored in: `from` inclusive, `to` exclusive. Two segments that
 * merely share a boundary number (predecessor `to = X`, successor `from = X`)
 * therefore do NOT overlap — the successor owns day `X`, the predecessor does
 * not. This is the correct test for the uniqueness-key overlap advisory; the
 * closed-range {@link validityOverlaps} stays for the (non-segment) belief
 * contradiction rule. An absent bound is unbounded in that direction.
 */
export function validityOverlapsHalfOpen(
  a: FactValidity | null,
  b: FactValidity | null,
): boolean {
  if (a === null || b === null) return true; // timeless overlaps everything
  const aFrom = a.from ?? Number.NEGATIVE_INFINITY;
  const aTo = a.to ?? Number.POSITIVE_INFINITY;
  const bFrom = b.from ?? Number.NEGATIVE_INFINITY;
  const bTo = b.to ?? Number.POSITIVE_INFINITY;
  // Half-open: the intervals intersect iff each starts strictly before the
  // other ends. Sharing a boundary (aTo === bFrom) is NOT an overlap.
  return aFrom < bTo && bFrom < aTo;
}

/**
 * Stable structural equality for fact values (primitives, strings, refs as
 * strings, and the occasional plain record). JSON serialisation is sufficient
 * here — fact values are JSON-serialisable by construction (no Maps/Sets/Dates
 * in the substrate), and we only need "are these the same asserted value".
 */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}
